import { useEffect, useState } from "react";
import { Menu, X, UserCircle2, LogIn } from "lucide-react";
import { Logo } from "@/components/Logo";
import { photoSrc, initialsOf } from "@/components/ProfilePhoto";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Pillars", href: "#pillars" },
  { label: "Tournaments", href: "#tournaments" },
  { label: "Leaderboard", href: "#leaderboard" },
  { label: "One Talanta ID", href: "#one-id" },
];

export const TopNav = ({ onLogin, onSignin, onProfile }) => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-talanta-green/95 backdrop-blur-xl shadow-lg shadow-black/20"
          : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="h-1 kenya-stripe" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo dark onClick={() => (window.location.href = "/#home")} />

          <nav className="hidden md:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm font-medium text-white/80 hover:text-talanta-gold transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button
                data-testid="nav-one-id-btn"
                onClick={onProfile}
                className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 pl-1.5 pr-4 py-1.5 text-sm font-semibold text-white transition-colors"
              >
                {photoSrc(user) ? (
                  <img src={photoSrc(user)} alt="" className="h-7 w-7 rounded-full object-cover border border-talanta-gold/60" />
                ) : (
                  <span className="h-7 w-7 rounded-full bg-talanta-gold/20 text-talanta-gold text-xs font-bold flex items-center justify-center">{initialsOf(user.name)}</span>
                )}
                {user.name || user.talanta_id}
              </button>
            ) : (
              <>
                <button
                  data-testid="nav-signin-btn"
                  onClick={onSignin}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-talanta-gold transition-colors"
                >
                  <LogIn className="h-4 w-4" /> Sign in
                </button>
                <button
                  data-testid="nav-one-id-btn"
                  onClick={onLogin}
                  className="flex items-center gap-2 rounded-full bg-talanta-red hover:bg-talanta-red/90 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-talanta-red/30 transition-all hover:scale-[1.03]"
                >
                  <UserCircle2 className="h-4 w-4" />
                  Create your ID
                </button>
              </>
            )}
          </div>

          <button
            data-testid="nav-mobile-menu-trigger"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-talanta-green/98 backdrop-blur-xl border-t border-white/10 px-4 pb-5 pt-2">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-white/85 font-medium border-b border-white/5"
            >
              {l.label}
            </a>
          ))}
          <button
            data-testid="nav-one-id-btn-mobile"
            onClick={() => {
              setOpen(false);
              user ? onProfile() : onLogin();
            }}
            className="mt-4 w-full rounded-full bg-talanta-red py-3 text-white font-semibold"
          >
            {user ? `My One Talanta ID (${user.talanta_id})` : "Create your One Talanta ID"}
          </button>
          {!user && (
            <button
              data-testid="nav-signin-btn-mobile"
              onClick={() => {
                setOpen(false);
                onSignin();
              }}
              className="mt-2 w-full rounded-full border border-white/25 py-3 text-white font-semibold flex items-center justify-center gap-2"
            >
              <LogIn className="h-4 w-4" /> Sign in — I already have an ID
            </button>
          )}
        </div>
      )}
    </header>
  );
};
